import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { LoginRequest, AuthResponse, VerifyOtpRequest, User } from '../../types';
import { galleryService } from '../../services/galleryService';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  userId: string | null;
  requiresTwoFactor: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem('token'),
  user: null,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  userId: null,
  requiresTwoFactor: false,
  loading: false,
  error: null,
};

// Helper to set auth token in axios and galleryService
const setAuthToken = (token: string | null) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    galleryService.setToken(token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    galleryService.clearToken();
  }
};

// Initialize axios default header if token exists
if (initialState.token) {
  setAuthToken(initialState.token);
}

// Login with credentials (username/email + password)
export const login = createAsyncThunk(
  'auth/login',
  async ({ login, password }: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthResponse>(`${API_URL}/auth/login`, {
        login,
        password
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Login failed');
    }
  }
);

// Verify OTP for 2FA
export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ userId, otpCode }: VerifyOtpRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthResponse>(`${API_URL}/auth/verify-otp`, {
        userId,
        otpCode,
        purpose: '2FA_LOGIN'
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'OTP verification failed');
    }
  }
);

// Fetch user profile (full details including fullName)
export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.auth.token;
      const userId = state.auth.userId;
      if (!token || !userId) {
        return rejectWithValue('No token or user id');
      }
      const response = await axios.get(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data; // expects UserResponse object
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

// Initiate password reset (forgot password)
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to send OTP');
    }
  }
);

// Reset password with OTP
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, otpCode, newPassword }: { email: string; otpCode: string; newPassword: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        otpCode,
        newPassword
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to reset password');
    }
  }
);

// Setup 2FA
export const setupTwoFactor = createAsyncThunk(
  'auth/setupTwoFactor',
  async (userId: string, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.auth.token;
      const response = await axios.post(
        `${API_URL}/auth/setup-2fa?userId=${userId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to setup 2FA');
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = state.auth.token;
      if (token) {
        await axios.post(
          `${API_URL}/auth/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Logout failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetTwoFactor: (state) => {
      state.requiresTwoFactor = false;
      state.userId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthResponse>) => {
        state.loading = false;
        
        if (action.payload.requiresTwoFactor) {
          state.requiresTwoFactor = true;
          state.userId = action.payload.userId || null;
          notifications.show({
            title: 'Verification Required',
            message: action.payload.message || 'Please check your email for the verification code',
            color: 'blue',
          });
        } else if (action.payload.token) {
          state.isAuthenticated = true;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken || null;
          state.user = {
            id: action.payload.userId || '',
            username: action.payload.username || '',
            email: action.payload.email || '',
            phoneNumber: '',
            fullName: '',
            enabled: true,
            twoFactorEnabled: false,
            emailVerified: false,
            phoneVerified: false,
            roles: action.payload.roles || [],
            createdAt: new Date().toISOString(),
            lastLoginAt: null,
          };
          
          // Save to localStorage
          localStorage.setItem('token', action.payload.token);
          if (action.payload.refreshToken) {
            localStorage.setItem('refreshToken', action.payload.refreshToken);
          }
          
          // Set token in axios and galleryService
          setAuthToken(action.payload.token);
          
          notifications.show({
            title: 'Success',
            message: 'Logged in successfully',
            color: 'green',
          });
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Login failed';
        notifications.show({
          title: 'Error',
          message: state.error,
          color: 'red',
        });
      })
      
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action: PayloadAction<AuthResponse>) => {
        state.loading = false;
        
        if (action.payload.token) {
          state.isAuthenticated = true;
          state.requiresTwoFactor = false;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken || null;
          state.user = {
            id: action.payload.userId || '',
            username: action.payload.username || '',
            email: action.payload.email || '',
            phoneNumber: '',
            fullName: '',
            enabled: true,
            twoFactorEnabled: true,
            emailVerified: true,
            phoneVerified: false,
            roles: action.payload.roles || [],
            createdAt: new Date().toISOString(),
            lastLoginAt: null,
          };
          
          // Save to localStorage
          localStorage.setItem('token', action.payload.token);
          if (action.payload.refreshToken) {
            localStorage.setItem('refreshToken', action.payload.refreshToken);
          }
          
          // Set token in axios and galleryService
          setAuthToken(action.payload.token);
          
          notifications.show({
            title: 'Success',
            message: '2FA verification successful',
            color: 'green',
          });
        }
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Invalid OTP';
        notifications.show({
          title: 'Error',
          message: state.error,
          color: 'red',
        });
      })
      
      // Fetch User Profile
      .addCase(fetchUserProfile.pending, (state) => {
        // optional loading indicator for profile fetch
      })
      .addCase(fetchUserProfile.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload && state.user) {
          // Merge the fetched profile data into the existing user object
          state.user.fullName = action.payload.fullName || state.user.fullName;
          state.user.email = action.payload.email || state.user.email;
          state.user.phoneNumber = action.payload.phoneNumber || state.user.phoneNumber;
          // Also update other fields if needed
          if (action.payload.roles) state.user.roles = action.payload.roles;
          if (action.payload.enabled !== undefined) state.user.enabled = action.payload.enabled;
          if (action.payload.twoFactorEnabled !== undefined) state.user.twoFactorEnabled = action.payload.twoFactorEnabled;
          if (action.payload.emailVerified !== undefined) state.user.emailVerified = action.payload.emailVerified;
        }
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        console.error('Failed to fetch user profile:', action.payload);
      })
      
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        notifications.show({
          title: 'OTP Sent',
          message: 'Password reset OTP sent to your email',
          color: 'blue',
        });
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to send OTP';
        notifications.show({
          title: 'Error',
          message: state.error,
          color: 'red',
        });
      })
      
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        notifications.show({
          title: 'Success',
          message: 'Password reset successfully',
          color: 'green',
        });
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to reset password';
        notifications.show({
          title: 'Error',
          message: state.error,
          color: 'red',
        });
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.userId = null;
        state.requiresTwoFactor = false;
        state.loading = false;
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        setAuthToken(null);
        
        notifications.show({
          title: 'Logged Out',
          message: 'You have been successfully logged out',
          color: 'blue',
        });
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
        
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.userId = null;
        state.requiresTwoFactor = false;
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        setAuthToken(null);
      });
  },
});

export const { clearError, resetTwoFactor } = authSlice.actions;
export default authSlice.reducer;